import * as React from 'react';
import { cn } from '../utils';

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
export type AvatarShape = 'circle' | 'square';
export type AvatarRing = 'none' | 'bg' | 'accent';
export type AvatarStatus = 'online' | 'offline' | 'dnd' | 'away';

export interface AvatarProps {
  name: string;
  src?: string | null;
  size?: AvatarSize;
  shape?: AvatarShape;
  ring?: AvatarRing;
  status?: AvatarStatus;
  className?: string;
  children?: never;
}

export interface AvatarGroupProps {
  children: React.ReactNode;
  max?: number;
  size?: AvatarSize;
  className?: string;
}

const SIZE_CLASSES: Record<AvatarSize, { container: string; dot: string }> = {
  xs: { container: 'h-5 w-5 text-[10px]', dot: 'h-[6px] w-[6px]' },
  sm: { container: 'h-6 w-6 text-[10px]', dot: 'h-[6px] w-[6px]' },
  md: { container: 'h-8 w-8 text-[12px]', dot: 'h-[8px] w-[8px]' },
  lg: { container: 'h-10 w-10 text-[14px]', dot: 'h-[10px] w-[10px]' },
  xl: { container: 'h-14 w-14 text-[18px]', dot: 'h-[14px] w-[14px]' },
};

const TINT_VARS = [
  'var(--color-avatar-tint-1)',
  'var(--color-avatar-tint-2)',
  'var(--color-avatar-tint-3)',
  'var(--color-avatar-tint-4)',
  'var(--color-avatar-tint-5)',
  'var(--color-avatar-tint-6)',
  'var(--color-avatar-tint-7)',
  'var(--color-avatar-tint-8)',
];

function hashTint(name: string) {
  if (!name) return 0;
  let hash = 0;
  for (const char of name) {
    hash = (hash << 5) - hash + char.charCodeAt(0);
    hash |= 0;
  }
  return Math.abs(hash) % TINT_VARS.length;
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return (parts[0]?.[0] ?? '').toUpperCase();
  return `${parts[0]?.[0] ?? ''}${parts[1]?.[0] ?? ''}`.toUpperCase();
}

function StatusDot({ status, size }: { status?: AvatarStatus; size: AvatarSize }) {
  if (!status) return null;
  const colors: Record<AvatarStatus, string> = {
    online: 'bg-[var(--color-success)]',
    offline: 'bg-[var(--color-border)]',
    dnd: 'bg-[var(--color-error)]',
    away: 'bg-[var(--color-warning)]',
  };
  const dotClass = SIZE_CLASSES[size].dot;

  return (
    <span
      aria-label={status}
      title={status}
      className={cn(
        'absolute bottom-0 right-0 translate-x-1/6 translate-y-1/6 rounded-full border-2 border-[var(--color-bg)]',
        colors[status],
        dotClass,
      )}
    />
  );
}

export const Avatar = React.forwardRef<HTMLDivElement, AvatarProps>(
  ({ name, src, size = 'md', shape = 'circle', ring = 'none', status, className }, ref) => {
    const [imgFailed, setImgFailed] = React.useState(false);
    const showImage = !!src && !imgFailed;
    const initials = name ? getInitials(name) : '';
    const tint = TINT_VARS[hashTint(name)];
    const sizeClass = SIZE_CLASSES[size].container;

    const ringClass =
      ring === 'bg'
        ? 'ring-2 ring-[var(--color-bg)]'
        : ring === 'accent'
          ? 'ring-2 ring-[var(--color-accent)]'
          : '';

    const shapeClass = shape === 'square' ? 'rounded-[12px]' : 'rounded-full';

    return (
      <div
        ref={ref}
        role="img"
        aria-label={name || 'Avatar'}
        className={cn(
          'relative inline-flex shrink-0 items-center justify-center overflow-hidden',
          'text-[var(--color-text-primary)] border border-[var(--color-border)] font-medium select-none',
          shapeClass,
          sizeClass,
          ringClass,
          className,
        )}
        style={{ backgroundColor: tint }}
      >
        {showImage ? (
          <img
            src={src}
            alt={name || ''}
            className="h-full w-full object-cover"
            onError={() => setImgFailed(true)}
          />
        ) : (
          <span aria-hidden>{initials}</span>
        )}
        <StatusDot status={status} size={size} />
      </div>
    );
  },
);
Avatar.displayName = 'Avatar';

export function AvatarGroup({ children, max = 3, size = 'md', className }: AvatarGroupProps) {
  const items = React.Children.toArray(children).filter(Boolean);
  const showCount = Math.min(items.length, max);
  const overflow = items.length - showCount;
  const display = items.slice(0, showCount);

  return (
    <div className={cn('flex items-center', className)}>
      {display.map((child, index) => (
        <div
          key={index}
          className={cn('outline outline-2 outline-[var(--color-bg)]', index === 0 ? '' : '-ml-2')}
        >
          {React.isValidElement(child)
            ? React.cloneElement(child as React.ReactElement<AvatarProps>, { size })
            : child}
        </div>
      ))}
      {overflow > 0 ? (
        <div
          className={cn(
            'inline-flex shrink-0 items-center justify-center rounded-full border border-[var(--color-border)] bg-[var(--color-surface)] text-[11px] font-semibold text-[var(--color-text-primary)] outline outline-2 outline-[var(--color-bg)]',
            SIZE_CLASSES[size].container,
            display.length ? '-ml-2' : '',
          )}
        >
          +{overflow}
        </div>
      ) : null}
    </div>
  );
}
